import React, { useState, useRef, useEffect } from "react";
import "../App.css";
import { useLocation } from "react-router-dom";

function ExamQuestionNumbers({ onQuestionClick, answeredQuestions }) {
  const scrollContainerRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { assessment_id } = location.state || {};

  const buttonStyle = {
    width: "25%",
    backgroundColor: "#263F86",
    color: "#ffffff",
    fontFamily: "Manrope-Regular",
  };

  useEffect(() => {
    const fetchQuestionCount = async () => {
      try {
        if (!assessment_id) {
          setError("Assessment ID is missing.");
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API}/getExamQuestionCount?assessment_id=${assessment_id}`
        );
        const data = await response.json();

        if (data.success && Number.isInteger(data.count) && data.count > 0) {
          const questionNumbers = Array.from(
            { length: data.count },
            (_, index) => index + 1
          );
          setQuestions(questionNumbers);
          setSelectedQuestion(questionNumbers[0]);
          onQuestionClick(0);
        } else {
          setError(data.message || "No questions found for this assessment.");
        }
      } catch (error) {
        console.error("Error fetching question count:", error);
        setError("Failed to fetch question count. Please try again.");
      }
    };

    fetchQuestionCount();
  }, [assessment_id, onQuestionClick]);

  useEffect(() => {
    if (scrollContainerRef.current && questions.length > 0) {
      const selectedElement =
        scrollContainerRef.current.children[currentIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentIndex, questions]);

  const scroll = (direction, step = 1) => {
    const scrollAmount = 100 * step;

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }

    if (direction === "next" && currentIndex < questions.length - 1) {
      const nextIndex = Math.min(currentIndex + step, questions.length - 1);
      setCurrentIndex(nextIndex);
      setSelectedQuestion(questions[nextIndex]);
      onQuestionClick(nextIndex);
    } else if (direction === "prev" && currentIndex > 0) {
      const prevIndex = Math.max(currentIndex - step, 0);
      setCurrentIndex(prevIndex);
      setSelectedQuestion(questions[prevIndex]);
      onQuestionClick(prevIndex);
    }
  };

  const handleQuestionClick = (question, index) => {
    setSelectedQuestion(question);
    setCurrentIndex(index);
    onQuestionClick(index);
  };

  if (error) {
    return <p className="text-danger text-center">{error}</p>;
  }

  if (questions.length === 0) {
    return <p className="text-center">Loading questions...</p>;
  }

  return (
    <>
      <div className="d-flex align-items-center">
        <button
          onClick={() => scroll("prev", 10)}
          className="btn btn-outline-secondary mx-2"
        >
          &#8592;
        </button>
        <div
          ref={scrollContainerRef}
          className="question-numbers-container d-flex overflow-auto"
          style={{ fontFamily: "Manrope-Regular" }}
        >
          {questions.map((question, index) => (
            <div
              key={index}
              className={`question-number-box border rounded px-3 py-2 mx-1 ${
                answeredQuestions[index] ? "answered" : ""
              } ${selectedQuestion === question ? "selected" : ""}`}
              onClick={() => handleQuestionClick(question, index)}
            >
              {question}
            </div>
          ))}
        </div>
        <button
          onClick={() => scroll("next", 10)}
          className="btn btn-outline-secondary mx-2"
        >
          &#8594;
        </button>
      </div>
      <footer className="custom-container bg-light py-3 mt-auto position-fixed w-100 bottom-0">
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <button
          onClick={() => scroll("prev")}
          type="button"
          className="btn mx-2 mobile-questions"
          style={buttonStyle}
        >
          &#8592; Prev
        </button>
        <button
          onClick={() => scroll("next")}
          type="button"
          className="btn mx-2 mobile-questions"
          style={buttonStyle}
        >
          Next &#8594;
        </button>
      </div>
    </footer>
    </>
  );
}

export default ExamQuestionNumbers;
